/**
 * One-Time Purchase Adapters
 *
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 *
 * Builds a My Products card for every one-time invoice line the account holds.
 *
 * The shop bills two ways. A subscription is described by the customer's Stripe
 * subscription records and is adapted in ./subscription-adapter.ts. Everything
 * else is described only by an invoice line -- Document Translator word credits
 * are the live example -- and nothing in the subscription record mentions it.
 * A purchase adapted by neither is a purchase the customer paid for and cannot
 * see, so this module is the second half of the catalogue rather than a variant
 * of the first.
 *
 * FCR S291 Issue 8 (DD-3): all Stripe field reads use the dual-path helpers
 * in ./_stripe-helpers.ts so the adapter works against both legacy
 * (<=2025-12) and API 2026-04-22.dahlia shapes. Direct ``item.price.product``
 * / ``invoice.payment_intent`` reads return null under v15 and would render
 * every card as "No license key" / "No Devices".
 */

import {
  countDistinctProducts,
  filterLicensesByProduct,
  filterLicensesByPriceId,
} from "@/lib/license-matching";
import { License, Order } from "@/types";
import { fetchLicense, fetchProductName } from "@/utils/dashboardUtils";
import { getProductDisplayName } from "@/lib/utils";
import { logger } from "@/lib/logger";
import { isTslExtraWordsProduct } from "@/lib/cart-limits";
import type { OneTimePurchase } from "./adapter-types";
import {
  getInvoicePaymentIntent,
  getLineProductId,
  getLinePriceId,
  getLineType,
} from "./_stripe-helpers";
import {
  DOCUMENT_TRANSLATOR_PRODUCT_ID,
  isDocumentTranslator,
} from "@/lib/document-translator-pricing";
import { getPurchasedDeviceCount } from "@/lib/licensing";

/**
 * An invoice line carrying the two invoice-level facts the card needs, so the
 * grouping and sorting below never has to hold on to the invoice as well.
 */
type OneTimeLine = Order["lines"]["data"][number] & {
  /** Resolved through the dual-path helper; null on transitional invoices. */
  payment_intent: string | null;
  /**
   * SU-13 (FCR S557): the PURCHASE date, in Unix seconds.
   *
   * This is deliberately not ``period.end``. That field was read here until
   * S652 and is the end of the billing period the line was created against,
   * which for a one-time line on a subscription invoice is a year ahead of the
   * purchase -- rendered by My Products under the heading "Purchased".
   * ``period.start`` is the purchase instant for a one-time line, and the
   * invoice's own creation date is the fallback for a line that carries no
   * period at all.
   */
  purchasedAt: number;
};

export const adaptOneTimePurchases = async (
  invoices: Order[],
): Promise<OneTimePurchase[]> => {
  if (!invoices || invoices.length === 0) {
    logger.info("No invoices found - returning empty one-time purchases");
    return [];
  }

  const oneTimeLineItems: OneTimeLine[] = invoices.flatMap((invoice) => {
    if (!invoice?.lines?.data || !Array.isArray(invoice.lines.data)) {
      return [];
    }

    // H-4 (FCR S557): a line this adapter cannot classify is dropped, and the
    // subscription adapter drops it too, so a paid product can leave the account
    // with nothing recording that it existed. Count them and report once per
    // load, naming the lines, so a support call has something to look at.
    const unclassified = invoice.lines.data.filter(
      (item) => getLineType(item) === undefined,
    );
    if (unclassified.length > 0) {
      logger.warn(
        `[OneTime] ${unclassified.length} invoice line(s) on ${invoice.id ?? "unknown invoice"} ` +
          `could not be classified as one-time or recurring and are not shown: ` +
          unclassified
            .map((item) => getLinePriceId(item) ?? "unknown price")
            .join(", "),
      );
    }

    const invoiceCreated = (invoice as { created?: number }).created ?? 0;
    const paymentIntent = getInvoicePaymentIntent(invoice);

    return (
      invoice.lines.data
        // FCR S291 Issue 8 / H-1 (FCR S557): getLineType reads the expanded price
        // object first, then the interval, then the legacy fields.
        .filter((item) => getLineType(item) === "one_time")
        // GP-8 (Pass 20): a word pack is not a licence. It is a consumable added
        // to an existing Text Speaker Live licence, and the customer reads it on
        // the word balance panel and in billing history. Given a card of its own
        // it would show "0 license keys" and a dead View License control, which
        // describes a broken purchase rather than a topped-up balance.
        //
        // Document Translator is the opposite case and is deliberately kept: it
        // is a product the customer owns outright rather than an addition to
        // something else, and My Products presents it as Pay-per-use with the
        // licence fields marked not-applicable.
        .filter((item) => !isTslExtraWordsProduct(getLineProductId(item) ?? ""))
        // FCR S291 Issue 8: getInvoicePaymentIntent walks the v15 nested
        // payments.data[0].payment.payment_intent path when the legacy
        // top-level field is null.
        .map((item) => ({
          ...item,
          payment_intent: paymentIntent,
          purchasedAt:
            (item as { period?: { start?: number } }).period?.start ??
            invoiceCreated,
        }))
    );
  });

  if (oneTimeLineItems.length === 0) {
    logger.info("No one-time line items found in invoices");
    return [];
  }

  // NF-3: group by productId:priceId, so two line items for one product bought
  // at two different prices stay on two cards rather than merging into one.
  const groups = new Map<string, OneTimeLine[]>();
  for (const item of oneTimeLineItems) {
    // FCR S291 Issue 8: dual-path productId / priceId resolution.
    const productId = getLineProductId(item);
    if (!productId) {
      continue;
    }
    const priceId = getLinePriceId(item) || "";
    // S-4: Document Translator is billed through an inline Stripe ``price_data``
    // line, so every purchase above the minimum word count carries a different
    // anonymous price id. Grouping it by price would give the customer one card
    // per purchase. Group by productId only so all of it is one card.
    const groupKey = isDocumentTranslator(productId)
      ? productId
      : priceId
        ? `${productId}:${priceId}`
        : productId;
    if (!groups.has(groupKey)) {
      groups.set(groupKey, []);
    }
    groups.get(groupKey)!.push(item);
  }

  // N-10 (FCR S652): sort, so that ``group[0]`` is the most recent purchase.
  // The card takes its id, its price and its date from the first element, and
  // until this session a comment asserted this ordering while nothing produced
  // it -- group[0] was whichever line the invoice list happened to return
  // first. A comment that states a guarantee nothing provides is worse than no
  // comment: it is what stops the next reader checking.
  for (const items of groups.values()) {
    items.sort((x, y) => y.purchasedAt - x.purchasedAt);
  }

  const productPromises = Array.from(groups.entries()).map(
    async ([groupKey, group]) => {
      // S295/DD-1: pull productId from the same getter the group key was built
      // from. The earlier `if (!productId) continue` guard above ensures every
      // grouped item has a string productId, so `getLineProductId(group[0])`
      // returns string here too -- no `as string` cast needed.
      const productId = getLineProductId(group[0]);
      if (!productId) {
        // Defensive: cannot happen given the grouping loop above, but typing
        // this as `string | undefined` requires a runtime check before use.
        return null;
      }

      const licensesNested: License[][] = await Promise.all(
        group.map(async (item) => {
          // S295/BE-2: `payment_intent` is `string | null` since helpers were
          // extracted (some Stripe transitional invoices on the new API --
          // refunded, zero-amount subscription -- return a null
          // payment_intent). `fetchLicense` requires a string, so the call
          // would type-error without this guard, and the licenses scoped to
          // a null payment intent cannot be retrieved anyway.
          if (!item.payment_intent) {
            logger.warn(
              `No payment intent for product ${productId} - skipping license fetch`,
            );
            return [];
          }
          try {
            const licenses = await fetchLicense(item.payment_intent, productId);
            return Array.isArray(licenses) ? licenses : [];
          } catch (error) {
            logger.error(
              `Failed to fetch license for payment intent ${item.payment_intent}:`,
              error,
            );
            return [];
          }
        }),
      );

      // Build per-license quantity map before flattening.
      // group[i] is the purchase that generated licensesNested[i], so we can
      // associate each ActivationKey with its exact Stripe quantity.
      // Without this, group[0].quantity is applied to every license regardless
      // of which purchase created it, producing inflated device counts when a
      // user has made multiple purchases with different quantities.
      const licenseQuantityMap = new Map<string, number>();
      licensesNested.forEach((licensesForPurchase, i) => {
        const qty = group[i].quantity ?? 1;
        licensesForPurchase.forEach((l) => {
          if (l.ActivationKey) licenseQuantityMap.set(l.ActivationKey, qty);
        });
      });

      const flatLicenses = licensesNested.flat();
      // EX-2 (S589): count products, not keys. The previous line counted licences
      // on the reasoning that one purchase yields one key -- but a customer who
      // buys the same product twice holds two keys for one product, and counting
      // those as two products switched on the cross-product guard and hid the
      // licence they were looking at.
      const totalProductsInOrder = countDistinctProducts(flatLicenses);
      const matchResult = filterLicensesByProduct(
        flatLicenses,
        productId,
        totalProductsInOrder,
      );
      const licenses = matchResult.licenses;

      // If no matching licenses found from payment-intent-scoped fetch, show 0
      // rather than widening the search to ALL user licenses via fetchLicenses().
      // The wider search caused cross-order contamination -- licenses from
      // completely unrelated past orders could appear under the wrong product.
      if (licenses.length === 0 && flatLicenses.length > 0) {
        logger.warn(
          `[OneTime ${productId}] No matching licenses from payment intent ` +
            `(${flatLicenses.length} fetched, 0 matched). ` +
            `Check UserData1 population in QLM.`,
        );
      }

      if (matchResult.legacyMatchCount > 0) {
        logger.info(
          `[OneTime ${productId}] ${matchResult.legacyMatchCount} legacy license(s) matched by ProductID`,
        );
      }

      // S256 (FCR §3): every line reaching this point is `type: "one_time"` --
      // the filter above admits nothing else. Route the name through
      // getProductDisplayName anyway, so a one-time card is named by the same
      // rule as every other surface rather than by a second one written here.
      const baseProductName = await fetchProductName(productId);
      const productName = getProductDisplayName(
        baseProductName,
        "one_time",
        undefined,
        productId,
      );

      // S-4: a Document Translator card deliberately spans several anonymous
      // prices, so there is no single price that identifies it and filtering by
      // one would hide the other purchases' licences. Every other card is one
      // price by construction.
      const rowPriceId = isDocumentTranslator(productId)
        ? ""
        : getLinePriceId(group[0]) || "";

      // VD-2 (Pass 20, corrected S656): narrow to this row's price BEFORE the
      // seat figures are summed. VD-2 narrowed the licence list the dialog reads
      // and left the three numbers printed beside it summed over both rows, so
      // on the one shape this filter exists for -- two line items of one product
      // at two prices in one order -- each row printed the other row's seats as
      // well as its own. Every figure the row shows now comes from one list.
      const rowLicenses = filterLicensesByPriceId(licenses, rowPriceId);

      let totalDevices = 0;
      let availableDevices = 0;
      rowLicenses.forEach((license) => {
        // SU-12 / SU-14 (FCR S557, S652): every customer-facing device figure is
        // the purchased quantity. QLM records one extra activation seat on every
        // licence so a customer can reinstall after a hardware change without
        // contacting support. That seat is real in QLM and must never be shown
        // and never grant an activation, so getPurchasedDeviceCount subtracts
        // it: a fifty-seat purchase is fifty-one in QLM and fifty on screen.
        //
        // Where QLM reports no seat data at all, the Stripe quantity is the
        // purchased figure directly -- it is what the customer bought, with no
        // buffer applied.
        const stripeQuantity = licenseQuantityMap.get(license.ActivationKey) ?? 1;
        const purchased =
          license.MaxActivations != null || license.NumLicenses != null
            ? getPurchasedDeviceCount(license)
            : Math.max(1, stripeQuantity);
        totalDevices += purchased;
        // AvailableLicenses is QLM's own figure and counts the buffer seat, so
        // it cannot be used here. Derive availability from the purchased
        // quantity, clamped so a QLM over-report cannot go negative.
        availableDevices += Math.max(
          0,
          purchased - (license.ActivationCount ?? 0),
        );
      });

      // SU-10 (FCR S557): the Overview reads this figure. It shows what the
      // customer bought, not the internal seat count.
      const totalLicensesCount = rowLicenses.reduce((sum, license) => {
        const stripeQuantity = licenseQuantityMap.get(license.ActivationKey) ?? 1;
        return (
          sum +
          (license.MaxActivations != null || license.NumLicenses != null
            ? getPurchasedDeviceCount(license)
            : Math.max(1, stripeQuantity))
        );
      }, 0);

      return {
        id:
          group[0].payment_intent ||
          (group[0] as { id?: string }).id ||
          `${productId}:${getLinePriceId(group[0]) || ""}`,
        // VD-2 / GP-7: the group key already tells one row of a product from
        // another. It was built for grouping and then discarded, so the licence
        // dialog -- keyed on the product -- opened whichever row the adapters
        // emitted first. `id` is left as it is because it carries the payment
        // intent, which other paths read.
        rowKey: groupKey,
        productId: productId,
        productName: productName,
        // S657, "from N purchases": the number of purchases behind this card. The group key
        // is productId:priceId, so every entry in `group` is one purchase of
        // this card's product at this card's price.
        purchaseCount: group.length,
        // VD-2: only the licences belonging to this card's price, for the case
        // where one product legitimately produces two rows -- and the same list
        // the seat figures above were summed from.
        licenses: rowLicenses,
        totalLicenses: totalLicensesCount,
        totalDevices: totalDevices,
        availableDevices: availableDevices,
        // SU-13 / N-10 (S652): the purchase date of the most recent item in the
        // group, which the sort above guarantees is group[0].
        createdAt: group[0].purchasedAt,
      };
    },
  );

  // S295/DD-1: filter out the defensive `null` returns from the productId guard
  // above so the public return type `OneTimePurchase[]` holds.
  const results = await Promise.all(productPromises);
  const owned = results.filter((r) => r !== null) as OneTimePurchase[];

  // SU-29 (FCR S557): a refunded purchase is no longer an entitlement the
  // customer owns, so it is not listed on My Products or License Details.
  //
  // This replaces CHANGE_06 (FCR S483), which surfaced fully refunded invoices
  // here with a "refunded" label and a "Buy again" link. That was written for
  // cancelled subscriptions, where the customer keeps something until the paid
  // period ends. A refund is different: the licence is revoked at the moment
  // the money is returned, and listing the product among things the customer
  // owns describes an entitlement that no longer exists.
  //
  // The financial record is deliberately kept. The invoice and its refund stay
  // visible in invoice history permanently -- a customer who refunded still
  // needs the record of what they paid and what came back, and support needs it
  // more. Removing the entitlement is not the same as removing the history.
  //
  // SU-23 (FCR S637): counted per PURCHASE, not per product.
  //
  // Purchases of one product are grouped into a single card across all of the
  // customer's invoices, so a product-id filter removed the whole card as soon
  // as ANY order for it had been refunded. A customer who bought two seats on
  // two orders and refunded one lost both -- including the licence key for the
  // seat they still own, which is not reachable anywhere else in the shop.
  //
  // The card is removed only when every purchase behind it was refunded. When
  // some were and some were not, the customer still owns something and the card
  // stays. Counting per invoice is the finest unit the data supports:
  // `refunded_product_ids` says WHICH product was refunded on an invoice, not
  // how many seats of it, so a partial refund within one order keeps the card --
  // which is the right way round, because that customer does still own a seat.
  const purchasesByProduct = new Map<string, number>();
  const refundedByProduct = new Map<string, number>();
  for (const invoice of invoices) {
    const refundedIds = new Set(
      ((invoice as { refunded_product_ids?: string[] }).refunded_product_ids ??
        []
      ).filter(Boolean),
    );
    const seenOnThisInvoice = new Set<string>();
    for (const line of invoice.lines?.data ?? []) {
      const productId = getLineProductId(line) as string | undefined;
      if (!productId || seenOnThisInvoice.has(productId)) continue;
      seenOnThisInvoice.add(productId);
      purchasesByProduct.set(
        productId,
        (purchasesByProduct.get(productId) ?? 0) + 1,
      );
      if (refundedIds.has(productId)) {
        refundedByProduct.set(
          productId,
          (refundedByProduct.get(productId) ?? 0) + 1,
        );
      }
    }
  }

  const refundedProductIds = new Set<string>();
  for (const [productId, refundedCount] of refundedByProduct) {
    const purchaseCount = purchasesByProduct.get(productId) ?? 0;
    if (purchaseCount > 0 && refundedCount >= purchaseCount) {
      refundedProductIds.add(productId);
    }
  }

  if (refundedProductIds.size === 0) return owned;

  const stillOwned = owned.filter(
    (product) => !refundedProductIds.has(product.productId),
  );
  const removed = owned.length - stillOwned.length;
  if (removed > 0) {
    logger.info(
      `[OneTime] SU-23: ${removed} refunded product(s) removed from My Products -- ` +
        `a refunded purchase is not an entitlement the customer still owns.`,
    );
  }
  return stillOwned;
};

/**
 * Session 883 review, OV-1: the Document Translator row of a customer who has
 * only free words. It has the shape of a paid row and no licences. `id` holds no
 * payment reference, because `id` is read as one elsewhere.
 */
export const adaptFreeWordsRow = async (): Promise<OneTimePurchase> => {
  const productId = DOCUMENT_TRANSLATOR_PRODUCT_ID;
  return {
    id: "dt-free-words",
    rowKey: "dt-free-words",
    productId,
    productName: getProductDisplayName(
      await fetchProductName(productId),
      "one_time",
      undefined,
      productId,
    ),
    licenses: [],
    totalLicenses: 0,
    totalDevices: 0,
    availableDevices: 0,
  };
};
